import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '@/lib/firebase';

const TAG = 'admin/alerts/useAlertCount';

/**
 * Listener real-time da contagem de alertas pendentes (não
 * acknowledged) E severity != LOW. LOW não conta como urgente —
 * aparecem na lista mas não inflam o badge.
 *
 * Como Firestore não permite "WHERE acknowledgedAt IS NULL"
 * direto, filtramos client-side, mas pré-filtramos por severity
 * server-side para reduzir tráfego.
 *
 * Origem: sem issue — commit d4e0990a9, 2026-05-04 (badge de alertas no menu).
 *
 * Contrato: sem entrada → número de alertas HIGH/MEDIUM com `acknowledgedAt`
 * nulo ou ausente, ao vivo. Erro do listener → 0 só com `console.warn`: o
 * badge some sem report ao PostHog (achado registrado na #926).
 *
 * Usado por: `AlertCountBadge.tsx`, montado no item "alertas" do menu em
 * `routes/AdminLayout.tsx` (web `apps/admin`, toda tela autenticada).
 *
 * Arquitetura: `match /aggregations/alerts/items/{alertId}` de
 * `firebase/firestore.rules` (`isSuperAdmin()`); `severity in` sozinho usa o
 * índice automático de coleção. O listener vive enquanto o layout estiver
 * montado — é o único listener permanente do admin.
 *
 * Dicas: "não permite IS NULL" acima não é exato — `where(campo, '==', null)`
 * existe; combinar com `severity in` exigiria índice composto (registrado na
 * #926). Os literais `'HIGH'`/`'MEDIUM'` deveriam vir de `AlertSeveritySchema`.
 */
export function useAlertCount(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const ref = collection(firestore, 'aggregations', 'alerts', 'items');
    const q = query(ref, where('severity', 'in', ['HIGH', 'MEDIUM']));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        let pending = 0;
        for (const d of snap.docs) {
          const data = d.data() as Record<string, unknown>;
          if (data.acknowledgedAt === null || data.acknowledgedAt === undefined) {
            pending++;
          }
        }
        setCount(pending);
      },
      (err) => {
        console.warn(`${TAG}: snapshot failed`, err);
        setCount(0);
      },
    );
    return (): void => {
      unsubscribe();
    };
  }, []);

  return count;
}
